"use client";

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import * as Sentry from "@sentry/nextjs";
import Branding from "./Branding";

// Holds the config data plus a `refresh` used to re-read it after the admin
// edits branding, so the sidebar/tab update live without a full reload.
const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
	const [config, setConfig] = useState(null);

	const load = useCallback(async ({ initial = false } = {}) => {
		try {
			const res = await fetch("/api/config");
			const data = await res.json();
			// Phase-2 init: reinitialise with real DSN + environment. Only on the
			// first load and outside development so local dev stays noise-free.
			if (initial && data.sentryDsn && data.env !== "development") {
				Sentry.init({
					dsn: data.sentryDsn,
					environment: data.env,
					tracesSampleRate: 0.1,
					debug: false,
					sendDefaultPii: false,
					// Drop token/DNI-bearing query strings before events reach
					// the US-hosted Sentry (Ley 25.326 cross-border transfer).
					beforeSend(event) {
						if (event.request?.url) {
							const queryIndex = event.request.url.indexOf("?");
							if (queryIndex !== -1) {
								event.request.url = event.request.url.slice(
									0,
									queryIndex,
								);
							}
						}
						if (event.request?.headers) {
							delete event.request.headers.Cookie;
							delete event.request.headers.cookie;
						}
						return event;
					},
				});
			}
			setConfig(data);
		} catch {
			setConfig((prev) => prev ?? {});
		}
	}, []);

	useEffect(() => {
		load({ initial: true });
	}, [load]);

	if (!config) {
		return (
			<div className="flex items-center justify-center h-dvh bg-zinc-100 dark:bg-zinc-950">
				<div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-zinc-800 dark:border-zinc-200" />
			</div>
		);
	}

	return (
		<ConfigContext.Provider value={{ ...config, refresh: load }}>
			<Branding config={config} />
			{children}
		</ConfigContext.Provider>
	);
}

export function useConfig() {
	const ctx = useContext(ConfigContext);
	if (!ctx) throw new Error("useConfig must be used inside ConfigProvider");
	return ctx;
}
